import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { glintScatter, groundCloud, ringBlast } from './burstBuilders';
import { driftMotes, orbiter, pulsingShell } from './statusAuraBuilders';
import { PLAYER_HITBOX } from '../../../../shared/logic/CombatAnchors';

// =============================================================================
// PRIEST SUPPORT VFX — Brightstep (lane AM, 2026-10-08), the priest's
// friendly run-speed buff. Original look: warm lantern light pooled at the
// feet with sparks circling the ankles and motes kicked up behind — no wings,
// no feathers. Anchors: (0,0) is the centre of the 40×40 hitbox; the feet are
// at +FEET_Y. Transform/alpha tweens only.
// =============================================================================

const FEET_Y = PLAYER_HITBOX.height / 2;

/** Brightstep palette — lantern gold, candle white, a soft amber edge. */
const LANTERN = { gold: 0xFFE08A, white: 0xFFF8E7, amber: 0xF5B94A } as const;

/** Brightstep cast — light pools at the recipient's feet and flares once.
 *  Symmetric (also the remote cast beat). */
const BRIGHTSTEP_CAST: VFXEffectConfig = {
  id: 'brightstep_cast',
  name: 'Brightstep',
  category: 'burst',
  description: 'Lantern light pooling at the feet where Brightstep lands',
  primitives: [
    { ...groundCloud({ width: 64, height: 12, color: LANTERN.gold, alpha: 0.6, depth: 93,
      swellTo: 1.6, durationMs: 420 }), offsetY: FEET_Y },
    { ...ringBlast({ radius: 14, color: LANTERN.white, alpha: 0.75, glow: true, depth: 95,
      stroke: { color: LANTERN.amber, width: 2 }, expandTo: 2.2, durationMs: 340 }), offsetY: FEET_Y - 2 },
    { ...glintScatter({ count: 8, spread: 26, color: LANTERN.white, radius: 2.2, durationMs: 360 }), offsetY: FEET_Y - 10 },
  ],
};

/** Brightstep aura — while the buff lasts: a warm pool under the feet, two
 *  sparks circling the ankles, and motes rising off the heels so a runner
 *  leaves a short trail of light. Follows the recipient. */
const BRIGHTSTEP_AURA: VFXEffectConfig = {
  id: 'brightstep',
  name: 'Brightstep',
  category: 'status',
  description: 'Lantern light at the feet while Brightstep speeds a runner',
  followTarget: 'player',
  primitives: [
    pulsingShell({
      width: 50, height: 10, offsetY: FEET_Y + 1, color: LANTERN.gold, alpha: 0.35, glow: true,
      stroke: { color: LANTERN.amber, width: 1.5 },
      pulse: { scale: 1.12, alpha: 0.18, duration: 380 },
    }),
    orbiter({
      radius: 2.6, color: LANTERN.white, alpha: 0.95, glow: true, offsetY: FEET_Y - 3,
      orbit: { radius: 18, speed: 3.2 }, flicker: { alpha: 0.5, duration: 260 },
    }),
    orbiter({
      radius: 2.2, color: LANTERN.gold, alpha: 0.9, glow: true, offsetY: FEET_Y - 3,
      orbit: { radius: 18, speed: 3.2, startAngle: Math.PI }, flicker: { alpha: 0.5, duration: 300 },
    }),
    { ...driftMotes({
      direction: 'up', count: 5, spread: 16, radius: 2.2,
      color: LANTERN.white, alpha: 0.85, glow: true, travel: 26, duration: 520,
    }), offsetY: FEET_Y - 4 },
  ],
};

export const PRIEST_SUPPORT_VFX: VFXEffectConfig[] = [BRIGHTSTEP_CAST, BRIGHTSTEP_AURA];
